import { ApiError, createZone, getLocation, listZones } from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { CreateZoneRequest, LocationResponse, ZoneResponse } from '@jq4race/shared';
import { buttonVariants, Card, CardContent } from '@jq4race/ui';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { computeZoneCapacityHint } from '../../lib/zoneCapacityHint';
import { ZoneForm } from '../components/ZoneForm';

export function ZoneCreatePage() {
  const { t } = useTranslation('zones');
  const { locationId } = useParams({ strict: false });
  const navigate = useNavigate();

  // Arreglo 2: fetched purely to show the remaining-capacity hint next to
  // the capacity field (see computeZoneCapacityHint) — this location has no
  // zone of its own yet, so nothing is excluded from the sum. Two requests
  // (getLocation + listZones) are unavoidable: no endpoint returns both, and
  // this is the SAME pair ZonesIndexPage already fetches for its own
  // capacity summary. A silent failure here just hides the hint (it's a
  // nice-to-have), never blocks the form.
  const [location, setLocation] = useState<LocationResponse | null>(null);
  const [zones, setZones] = useState<ZoneResponse[]>([]);

  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    void Promise.all([getLocation(locationId), listZones(locationId)])
      .then(([locationRes, zonesRes]) => {
        if (cancelled) return;
        setLocation(locationRes);
        setZones(zonesRes.zones);
      })
      .catch(() => {
        // Hint is a nice-to-have; the create form still works without it.
      });
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  const capacityHint = computeZoneCapacityHint({
    locationCapacity: location?.vehicleCapacity ?? null,
    zones,
  });

  async function handleSubmit(values: CreateZoneRequest) {
    if (!locationId) return;
    try {
      await createZone(locationId, values);
      void navigate({ to: '/dashboard/locations/$locationId/zones', params: { locationId } });
    } catch (e) {
      if (e instanceof ApiError) {
        throw new LocalizedError(
          translateApiError(e, t, { fallback: t('page.create.createError') }),
        );
      }
      throw e;
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.create.title')}
        description={t('page.create.description')}
        actions={
          <Link
            to="/dashboard/locations/$locationId/zones"
            params={{ locationId: locationId ?? '' }}
            className={buttonVariants({ size: 'sm', variant: 'ghost' })}
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            {t('page.create.backToZones')}
          </Link>
        }
      />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <ZoneForm
            mode="create"
            onSubmit={handleSubmit}
            submitLabel={t('form.createZone')}
            capacityHint={capacityHint}
            onCancel={() =>
              void navigate({
                to: '/dashboard/locations/$locationId/zones',
                params: { locationId: locationId ?? '' },
              })
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
